/**
 * Lazy loader for the debug console (cmp-console.js). Only this small module
 * ships in cmp.js; the console UI itself is a separate asset.
 */
import { log } from "./debug";

import { CONSOLE_FLAG, CONSOLE_GLOBAL } from "./debug_keys";
export { CONSOLE_FLAG, CONSOLE_GLOBAL };

declare const __CONSOLE_VERSION__: string;
const consoleVersion = typeof __CONSOLE_VERSION__ === "string" ? __CONSOLE_VERSION__ : "dev";

type ConsoleHandle = { open: () => void; close: () => void };

function storage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

/**
 * true  → open (URL/hash `cmpconsole`, or persistent flag)
 * false → explicitly disabled via `cmpconsole=0|off|false` (also clears the flag)
 * null  → not requested
 */
export function consoleRequested(loc: Pick<Location, "search" | "hash"> = location): boolean | null {
  const m = /(?:^|[?&#])cmpconsole(?:=([^&#]*))?(?=$|[&#])/i.exec(`${loc.search || ""}${loc.hash || ""}`);
  if (m) {
    const off = /^(0|off|false|no)$/i.test(m[1] || "");
    setConsoleFlag(!off);
    return !off;
  }
  return storage()?.getItem(CONSOLE_FLAG) === "1" ? true : null;
}

export function setConsoleFlag(on: boolean) {
  const s = storage();
  if (!s) return;
  try { on ? s.setItem(CONSOLE_FLAG, "1") : s.removeItem(CONSOLE_FLAG); } catch { /* quota / privacy mode */ }
}

export function consoleUrl(apiBase: string) {
  return `${apiBase.replace(/\/$/, "")}/cmp/v1/cmp-console.js?v=${encodeURIComponent(consoleVersion)}`;
}

let loading = false;

export function openConsole(apiBase: string) {
  setConsoleFlag(true);
  const w = window as unknown as Record<string, ConsoleHandle | undefined>;
  if (w[CONSOLE_GLOBAL]) { w[CONSOLE_GLOBAL]!.open(); return; }
  if (loading) return;
  loading = true;
  const s = document.createElement("script");
  s.src = consoleUrl(apiBase);
  s.async = true;
  s.setAttribute("data-adcmp-internal", "console");
  s.onerror = () => { loading = false; log("error", "cmp-console.js failed to load", { src: s.src }); };
  log("console", "loading cmp-console.js");
  (document.head || document.documentElement).appendChild(s);
}

export function closeConsole() {
  setConsoleFlag(false);
  const w = window as unknown as Record<string, ConsoleHandle | undefined>;
  w[CONSOLE_GLOBAL]?.close();
}
